/**
 * Governed reasoning capability, and the boundary around changing it.
 *
 * Three things the registry holds apart, and this screen keeps apart too. A
 * **definition** says a capability exists and who is accountable. A **version**
 * says how it reasons, and stops changing the moment somebody qualifies it. A
 * **binding** says the capability is selected somewhere, and confers nothing
 * about that somewhere.
 *
 * The hard part is not the reading. It is that three of the seven authorities
 * here — qualify, promote, bind — are granted to nobody by the seeded baseline.
 * That is deliberate: deciding a capability may take part in decisions is a
 * different job from writing one. So this screen has to show a lifecycle most
 * callers cannot advance, without ever implying they could.
 *
 * Two rules do that work.
 *
 * **The lifecycle is information; the actions are affordances.** All six states
 * render for everyone. What authority changes is only whether a transition is
 * something you can do or something you can see.
 *
 * **An action nobody offered is absent, not disabled.** A greyed-out Qualify
 * button says *you could do this* while the platform says otherwise. Instead the
 * screen names the authority the operation needs and stops there — and because
 * that comes from the capability report rather than a hardcoded sentence, the
 * day somebody is granted `brain.version.qualify` the control simply appears.
 *
 * And where a promotion may go comes from the server, not from here. The
 * lifecycle permits `in_test → qualified`; a *promotion* may not perform it,
 * because qualification demands evidence, a recommendation, a rationale and an
 * actor who did not run the evaluation. `promote_targets` is the server saying
 * which of those transitions this operation can actually complete.
 */

import { useCallback, useState } from "react";
import { api } from "../api/client";
import { NexusError } from "../api/errors";
import type {
  BindingScope,
  BrainBinding,
  BrainDefinition,
  BrainVersion,
  CapabilityFamily,
  Page,
  RiskClass,
  VersionStatus,
} from "../api/types";
import { usePagedCollection, type PagedCollection } from "../api/useCollection";
import {
  Banner,
  Card,
  Classification,
  Fact,
  Facts,
  Identifier,
  Mono,
  More,
  Pill,
  when,
  type Tone,
} from "../components/primitives";
import { change, RequiredAuthority, Result, type Outcome } from "../components/governed";
import { Empty, Forbidden, Show } from "../components/states";
import { useSession } from "../session/SessionProvider";

const BRAIN = "/brain";

/** The whole lifecycle, always shown, whatever the caller may do to it. */
const LIFECYCLE: VersionStatus[] = [
  "draft",
  "in_test",
  "qualified",
  "active",
  "deprecated",
  "retired",
];

const STATUS_TONE: Record<VersionStatus, Tone> = {
  draft: "quiet",
  in_test: "wait",
  qualified: "mind",
  active: "ok",
  deprecated: "wait",
  retired: "quiet",
};

/** What each state means, so the rail is readable without the requirements. */
const STATUS_MEANING: Record<VersionStatus, string> = {
  draft: "Written, not yet evaluated.",
  in_test: "Evaluated, awaiting a governance decision.",
  qualified: "Judged fit to participate. What it means can no longer change.",
  active: "Available to be bound and used.",
  deprecated: "Still bound where it was, not for new use.",
  retired: "Closed. A retired capability grows no new versions.",
};

const FAMILIES: CapabilityFamily[] = [
  "requirements",
  "architecture",
  "backend",
  "frontend",
  "database",
  "security",
  "quality_assurance",
  "devops",
  "documentation",
  "regulatory_engineering",
];

const RISKS: RiskClass[] = ["routine", "elevated", "critical"];

const SCOPES: BindingScope[] = [
  "product",
  "application",
  "repository",
  "agent",
  "workflow_step",
];

/** 0 observe … 5 orchestrate. Named, because a bare integer says nothing. */
const AUTHORITY = ["observe", "recommend", "draft", "execute", "complete", "orchestrate"];

/**
 * A refusal, told apart from the other refusal.
 *
 * Two different `403`s reach this screen and they mean different things. One
 * says the authority went away between the projection and the press. The other
 * says the platform will not let *this person* make *this decision* — the
 * evaluator may not also qualify. Collapsing them would turn a governance rule
 * into what looks like a permissions glitch.
 */
function refusalOnThisScreen(error: unknown, whatWasRefused: string): string {
  if (error instanceof NexusError) {
    if (error.isForbidden) {
      const message = error.message.toLowerCase();
      if (message.includes("evaluation") || message.includes("qualify")) {
        return error.message;
      }
      return `Refused. ${whatWasRefused} is not permitted for you — this was offered on a decision that has since changed.`;
    }
    return error.message;
  }
  return error instanceof Error ? error.message : "The change did not happen.";
}

/**
 * The page size this screen has always asked for.
 *
 * Unchanged on purpose. What changes is not how much is read at once but
 * whether the hundred-and-first row is reachable at all: it used to be absent
 * without the screen ever saying so.
 */
const PAGE = 100;

/**
 * One registry collection, read the way every paged screen reads.
 *
 * The pagination is the shared primitive's, not this screen's. All three of
 * these endpoints take `limit` and `cursor` and answer with the platform's own
 * `has_more`, so nothing about memory or bindings has to be taught to it.
 *
 * `resetKey` is the path, because the path is what the accumulated pages
 * belong to. Selecting a different capability is a different collection of
 * versions, not more of the previous one.
 */
function useRegistryCollection<T>(path: string, enabled: boolean): PagedCollection<T> {
  const readPage = useCallback(
    (cursor: string | null, signal: AbortSignal) =>
      api.get<Page<T>>(path, {
        params: { limit: PAGE, cursor: cursor ?? undefined },
        signal,
      }),
    [path],
  );
  return usePagedCollection<T>(readPage, { enabled, resetKey: path });
}

// ── the lifecycle rail ────────────────────────────────────────────────────

function Lifecycle({ status }: { status: VersionStatus }) {
  return (
    <div style={{ display: "grid", gap: 6 }}>
      <ol
        className="nx-timeline"
        aria-label="Version lifecycle"
        style={{ display: "flex", flexWrap: "wrap", gap: 8, listStyle: "none", padding: 0 }}
      >
        {LIFECYCLE.map((state) => (
          <li key={state} aria-current={state === status ? "step" : undefined}>
            <Pill tone={state === status ? STATUS_TONE[state] : "quiet"}>
              {state === status ? `▸ ${state.replace(/_/g, " ")}` : state.replace(/_/g, " ")}
            </Pill>
          </li>
        ))}
      </ol>
      <p className="nx-hint" style={{ margin: 0 }}>
        {STATUS_MEANING[status]}
      </p>
    </div>
  );
}

// ── governance: qualify and promote ───────────────────────────────────────

function Qualification({
  brainKey,
  version,
  onChanged,
  report,
}: {
  brainKey: string;
  version: BrainVersion;
  onChanged: () => void;
  report: (outcome: Outcome) => void;
}) {
  const session = useSession();
  const [rationale, setRationale] = useState("");
  const [busy, setBusy] = useState(false);

  if (version.qualified_at !== null) {
    return (
      <Facts>
        <Fact label="Qualified">{when(version.qualified_at)}</Fact>
        <Fact label="Qualified by">
          <Identifier value={version.qualified_by ?? "—"} />
        </Fact>
        <Fact label="Rationale">{version.qualification_rationale ?? "—"}</Fact>
      </Facts>
    );
  }

  if (version.status !== "in_test") {
    return (
      <p className="nx-hint" style={{ marginTop: 10 }}>
        A version is qualified from <Mono>in_test</Mono>, on evidence that recommended it.
      </p>
    );
  }

  if (!session.may("brain_version.qualify")) {
    return <RequiredAuthority capability="brain.version.qualify" what="Qualification" />;
  }

  return (
    <form
      style={{ marginTop: 12 }}
      onSubmit={(event) => {
        event.preventDefault();
        setBusy(true);
        void change(
          (correlationId) =>
            api.post<BrainVersion>(
              `${BRAIN}/definitions/${brainKey}/versions/${String(version.version)}/qualification`,
              { rationale },
              { correlationId },
            ),
          "Qualifying a version",
          "Qualified. What this version means can no longer change.",
          refusalOnThisScreen,
        )
          .then((result) => {
            report(result);
            if (result.tone === "ok") {
              setRationale("");
              onChanged();
            }
          })
          .finally(() => {
            setBusy(false);
          });
      }}
    >
      <div className="nx-field">
        <label htmlFor="qualify-rationale">Qualification rationale</label>
        <input
          id="qualify-rationale"
          className="nx-input"
          required
          value={rationale}
          onChange={(event) => {
            setRationale(event.target.value);
          }}
        />
        <span className="nx-hint">
          A decision, not a test result. Whoever ran the evaluation may not also qualify it.
        </span>
      </div>
      <button type="submit" className="nx-button nx-button--primary" disabled={busy}>
        Qualify
      </button>
    </form>
  );
}

function Promotion({
  brainKey,
  version,
  onChanged,
  report,
}: {
  brainKey: string;
  version: BrainVersion;
  onChanged: () => void;
  report: (outcome: Outcome) => void;
}) {
  const session = useSession();
  const [busy, setBusy] = useState<string | null>(null);

  // Where the *server* says a promotion may go. Not the lifecycle table: the
  // lifecycle permits in_test → qualified and a promotion may not perform it.
  const targets = version.promote_targets;

  if (targets.length === 0) {
    return (
      <p className="nx-hint" style={{ marginTop: 10 }}>
        Nothing follows this state.
      </p>
    );
  }

  if (!session.may("brain_version.promote")) {
    return <RequiredAuthority capability="brain.version.promote" what="Status changes" />;
  }

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {targets.map((target) => (
          <button
            key={target}
            type="button"
            className="nx-button"
            disabled={busy !== null}
            onClick={() => {
              setBusy(target);
                void change(
                (correlationId) =>
                  api.post<BrainVersion>(
                    `${BRAIN}/definitions/${brainKey}/versions/${String(version.version)}/status`,
                    { target },
                    { correlationId },
                  ),
                "Moving this version",
                `Now ${target.replace(/_/g, " ")}.`,
                refusalOnThisScreen,
              )
                .then((result) => {
                  report(result);
                  if (result.tone === "ok") onChanged();
                })
                .finally(() => {
                  setBusy(null);
                });
            }}
          >
            Move to {target.replace(/_/g, " ")}
          </button>
        ))}
      </div>
    </div>
  );
}

function Evaluation({
  brainKey,
  version,
  onChanged,
  report,
}: {
  brainKey: string;
  version: BrainVersion;
  onChanged: () => void;
  report: (outcome: Outcome) => void;
}) {
  const session = useSession();
  const [form, setForm] = useState({
    suiteRef: "",
    suiteVersion: "",
    resultsRef: "",
    recommended: true,
  });
  const [busy, setBusy] = useState(false);

  if (version.evaluation !== null) {
    const evidence = version.evaluation;
    return (
      <Facts>
        <Fact label="Suite">
          <Mono>{evidence.suite_ref}</Mono> · {evidence.suite_version}
        </Fact>
        <Fact label="Results">
          <Mono>{evidence.results_ref}</Mono>
        </Fact>
        <Fact label="Executed">
          {when(evidence.executed_at)} by <Identifier value={evidence.executed_by} />
        </Fact>
        <Fact label="Recommended">
          <Pill tone={evidence.recommended ? "ok" : "bad"}>
            {evidence.recommended ? "yes" : "no"}
          </Pill>
        </Fact>
        {evidence.known_limitations.length > 0 ? (
          <Fact label="Known limitations">{evidence.known_limitations.join(" · ")}</Fact>
        ) : null}
      </Facts>
    );
  }

  if (!session.may("brain_version.declare")) {
    return <RequiredAuthority capability="brain.version.declare" what="Recording an evaluation" />;
  }

  return (
    <form
      style={{ marginTop: 12 }}
      onSubmit={(event) => {
        event.preventDefault();
        setBusy(true);
        void change(
          (correlationId) =>
            api.post(
              `${BRAIN}/definitions/${brainKey}/versions/${String(version.version)}/evaluation`,
              {
                evidence: {
                  suite_ref: form.suiteRef,
                  suite_version: form.suiteVersion,
                  results_ref: form.resultsRef,
                  executed_at: new Date().toISOString(),
                  executed_by: "",
                  recommended: form.recommended,
                },
              },
              { correlationId },
            ),
          "Recording an evaluation",
          "Recorded. The version is now in test.",
          refusalOnThisScreen,
        )
          .then((result) => {
            report(result);
            if (result.tone === "ok") onChanged();
          })
          .finally(() => {
            setBusy(false);
          });
      }}
    >
      <div className="nx-field">
        <label htmlFor="eval-suite">Evaluation suite</label>
        <input
          id="eval-suite"
          className="nx-input"
          required
          value={form.suiteRef}
          onChange={(event) => {
            setForm({ ...form, suiteRef: event.target.value });
          }}
        />
        <span className="nx-hint">A reference, never a transcript.</span>
      </div>
      <div className="nx-field">
        <label htmlFor="eval-suite-version">Suite version</label>
        <input
          id="eval-suite-version"
          className="nx-input"
          required
          value={form.suiteVersion}
          onChange={(event) => {
            setForm({ ...form, suiteVersion: event.target.value });
          }}
        />
      </div>
      <div className="nx-field">
        <label htmlFor="eval-results">Results</label>
        <input
          id="eval-results"
          className="nx-input"
          required
          value={form.resultsRef}
          onChange={(event) => {
            setForm({ ...form, resultsRef: event.target.value });
          }}
        />
      </div>
      <div className="nx-field">
        <label htmlFor="eval-recommended">Recommendation</label>
        <select
          id="eval-recommended"
          className="nx-select"
          value={form.recommended ? "yes" : "no"}
          onChange={(event) => {
            setForm({ ...form, recommended: event.target.value === "yes" });
          }}
        >
          <option value="yes">Recommended</option>
          <option value="no">Not recommended</option>
        </select>
      </div>
      <button type="submit" className="nx-button nx-button--primary" disabled={busy}>
        Record evaluation
      </button>
    </form>
  );
}

function VersionPanel({
  brainKey,
  version,
  onChanged,
  report,
}: {
  brainKey: string;
  version: BrainVersion;
  onChanged: () => void;
  report: (outcome: Outcome) => void;
}) {
  return (
    <Card title={`Version ${String(version.version)}`}>
      <div style={{ display: "grid", gap: 16 }}>
        <Lifecycle status={version.status} />

        <Facts>
          <Fact label="Behavioural contract">
            <Mono>{version.behavioral_contract_ref}</Mono>
          </Fact>
          <Fact label="Authority ceiling">
            {AUTHORITY[version.authority_ceiling] ?? String(version.authority_ceiling)}
          </Fact>
          <Fact label="Classification ceiling">
            <Classification value={version.classification_ceiling} />
          </Fact>
          {version.model_requirements.length > 0 ? (
            <Fact label="Model requirements">
              {version.model_requirements
                .map((need) => (need.minimum ? `${need.capability} ≥ ${need.minimum}` : need.capability))
                .join(" · ")}
            </Fact>
          ) : null}
          <Fact label="Declared">
            {when(version.recorded_at)} by <Identifier value={version.recorded_by} />
          </Fact>
        </Facts>

        <div>
          <h3 style={{ fontSize: "var(--nx-text-base)", margin: "0 0 4px" }}>Evaluation</h3>
          <Evaluation
            brainKey={brainKey}
            version={version}
            onChanged={onChanged}
            report={report}
          />
        </div>

        <div>
          <h3 style={{ fontSize: "var(--nx-text-base)", margin: "0 0 4px" }}>Qualification</h3>
          <Qualification
            brainKey={brainKey}
            version={version}
            onChanged={onChanged}
            report={report}
          />
        </div>

        <div>
          <h3 style={{ fontSize: "var(--nx-text-base)", margin: "0 0 4px" }}>Status</h3>
          <Promotion
            brainKey={brainKey}
            version={version}
            onChanged={onChanged}
            report={report}
          />
        </div>
      </div>
    </Card>
  );
}

// ── versions ──────────────────────────────────────────────────────────────

function DeclareVersion({
  brainKey,
  onDeclared,
  report,
}: {
  brainKey: string;
  onDeclared: () => void;
  report: (outcome: Outcome) => void;
}) {
  const [contractRef, setContractRef] = useState("");
  const [busy, setBusy] = useState(false);

  return (
    <form
      style={{ padding: "0 18px 18px" }}
      onSubmit={(event) => {
        event.preventDefault();
        setBusy(true);
        void change(
          (correlationId) =>
            api.post<BrainVersion>(
              `${BRAIN}/definitions/${brainKey}/versions`,
              { behavioral_contract_ref: contractRef },
              { correlationId },
            ),
          "Declaring a version",
          "Declared, as a draft.",
          refusalOnThisScreen,
        )
          .then((result) => {
            report(result);
            if (result.tone === "ok") {
              setContractRef("");
              onDeclared();
            }
          })
          .finally(() => {
            setBusy(false);
          });
      }}
    >
      <h3 style={{ fontSize: "var(--nx-text-base)", margin: "0 0 10px" }}>Declare the next version</h3>
      <div className="nx-field">
        <label htmlFor="version-contract">Behavioural contract</label>
        <input
          id="version-contract"
          className="nx-input"
          required
          value={contractRef}
          onChange={(event) => {
            setContractRef(event.target.value);
          }}
        />
        <span className="nx-hint">
          What this version is for and how it behaves — a reference the registry does not copy.
        </span>
      </div>
      <button type="submit" className="nx-button nx-button--primary" disabled={busy}>
        Declare
      </button>
    </form>
  );
}

function Versions({
  brainKey,
  selected,
  onSelect,
  collection,
  report,
  mayRead,
}: {
  brainKey: string;
  selected: number | null;
  onSelect: (version: number) => void;
  collection: PagedCollection<BrainVersion>;
  report: (outcome: Outcome) => void;
  mayRead: boolean;
}) {
  const session = useSession();
  // Reading a brain's versions is `brain.version.read`, which is not the
  // authority that let this screen list the definitions. Declaring one is a
  // third, so it stays offered here even when the list cannot be shown — a
  // caller who may write a version and not read the register is unusual, but
  // hiding the act would be the browser deciding something the server did not.
  if (!mayRead) {
    return (
      <Card title="Versions" bodyless>
        <div style={{ padding: "0 18px 18px" }}>
          <Forbidden what="this capability's versions" />
        </div>
        {session.may("brain_version.declare") ? (
          <DeclareVersion brainKey={brainKey} onDeclared={collection.refresh} report={report} />
        ) : null}
      </Card>
    );
  }
  return (
    <Card title="Versions" bodyless>
      <Show
        resource={collection.state}
        what="this capability's versions"
        onRetry={collection.refresh}
        empty={() =>
          collection.items.length === 0 ? (
            <Empty
              title="No versions yet."
              hint="A definition says a capability exists; a version says how it reasons."
            />
          ) : null
        }
      >
        {() => (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <caption className="nx-visually-hidden">Versions of this capability</caption>
              <thead>
                <tr>
                  <th scope="col">Version</th>
                  <th scope="col">Status</th>
                  <th scope="col">Declared</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {collection.items.map((version) => (
                  <tr key={version.brain_version_id}>
                    <td>
                      <span className="nx-table__primary">{version.version}</span>
                    </td>
                    <td>
                      <Pill tone={STATUS_TONE[version.status]}>
                        {version.status.replace(/_/g, " ")}
                      </Pill>
                    </td>
                    <td>{when(version.recorded_at)}</td>
                    <td>
                      <button
                        type="button"
                        className="nx-button"
                        aria-pressed={selected === version.version}
                        onClick={() => {
                          onSelect(version.version);
                        }}
                      >
                        {selected === version.version ? "Showing" : "Show"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Show>
      <More
        collection={collection}
        label="Show more versions"
        hint="This is one page of this capability's versions, not all of them."
        placement="bodyless"
      />
      {session.may("brain_version.declare") ? (
        <DeclareVersion brainKey={brainKey} onDeclared={collection.refresh} report={report} />
      ) : null}
    </Card>
  );
}

// ── bindings ──────────────────────────────────────────────────────────────

function Bindings({ brain, report }: { brain: BrainDefinition; report: (outcome: Outcome) => void }) {
  const session = useSession();
  const mayRead = session.may("brain_binding.read");
  const mayBind = session.may("brain_binding.bind");
  const bindings = useRegistryCollection<BrainBinding>(`${BRAIN}/bindings`, mayRead);
  const [form, setForm] = useState({
    version: "",
    scope: "repository" as BindingScope,
    targetId: "",
    purpose: "",
    rationale: "",
  });
  const [busy, setBusy] = useState(false);

  // The endpoint has no filter for a capability — its parameters are
  // `target_id`, `at`, `limit` and `cursor` — so the matching is done here,
  // over whatever pages have been read. Which is exactly why the absence of a
  // match is not evidence of anything until the walk has finished.
  const mine = bindings.items.filter((binding) => binding.brain_id === brain.brain_id);

  return (
    <Card title="Where it is used" bodyless>
      {mayRead ? (
        <Show
          resource={bindings.state}
          what="where capabilities are bound"
          onRetry={bindings.refresh}
          empty={() =>
            mine.length === 0 ? (
              // "Not bound anywhere" is a claim about the whole collection, and
              // only the end of the walk earns it. Said from a window that was
              // cut short it is a false negative: the binding may be sitting on
              // a page nobody has asked for yet.
              bindings.hasMore ? (
                <Empty
                  title="No binding for this capability on the pages read so far."
                  hint="Bindings are not read all at once, and this capability has not appeared in the ones read. Whether it is bound is not yet known."
                />
              ) : (
                <Empty
                  title="Not bound anywhere."
                  hint="A binding selects a capability for a purpose. Only an active version may be bound."
                />
              )
            ) : null
          }
        >
          {() => (
            <div className="nx-table-wrap">
              <table className="nx-table">
                <caption className="nx-visually-hidden">Bindings for this capability</caption>
                <thead>
                  <tr>
                    <th scope="col">Scope</th>
                    <th scope="col">Target</th>
                    <th scope="col">Version</th>
                    <th scope="col">Purpose</th>
                    <th scope="col">In force</th>
                  </tr>
                </thead>
                <tbody>
                  {mine.map((binding) => (
                    <tr key={binding.binding_id}>
                      <td>{binding.scope.replace(/_/g, " ")}</td>
                      <td>
                        <Identifier value={binding.target_id} />
                      </td>
                      <td>{binding.brain_version}</td>
                      <td>{binding.purpose}</td>
                      <td>
                        {when(binding.effective_from)}
                        {binding.effective_to ? ` until ${when(binding.effective_to)}` : " · open"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Show>
      ) : (
        <div className="nx-card__body">
          <Forbidden what="where capabilities are bound" />
          <p className="nx-hint">
            Reading where a capability is selected is granted apart from reading the registry.
          </p>
        </div>
      )}

      <More
        collection={bindings}
        label="Show more bindings"
        hint="Bindings are read a page at a time and matched to this capability here, so one this capability has may be on a page not yet read."
        placement="bodyless"
      />

      {mayBind ? (
        <form
          style={{ padding: "0 18px 18px" }}
          onSubmit={(event) => {
            event.preventDefault();
            setBusy(true);
            void change(
              (correlationId) =>
                api.post<BrainBinding>(
                  `${BRAIN}/bindings`,
                  {
                    key: brain.key,
                    version: Number(form.version),
                    scope: form.scope,
                    target_id: form.targetId,
                    purpose: form.purpose,
                    rationale: form.rationale,
                  },
                  { correlationId },
                ),
              "Binding a capability",
              "Bound.",
              refusalOnThisScreen,
            )
              .then((result) => {
                report(result);
                if (result.tone === "ok") {
                  setForm({ ...form, targetId: "", purpose: "", rationale: "" });
                  bindings.refresh();
                }
              })
              .finally(() => {
                setBusy(false);
              });
          }}
        >
          <h3 style={{ fontSize: "var(--nx-text-base)", margin: "0 0 10px" }}>Bind this capability</h3>
          <div className="nx-field">
            <label htmlFor="bind-version">Version</label>
            <input
              id="bind-version"
              className="nx-input"
              required
              inputMode="numeric"
              value={form.version}
              onChange={(event) => {
                setForm({ ...form, version: event.target.value });
              }}
            />
            <span className="nx-hint">Only an active version may be bound.</span>
          </div>
          <div className="nx-field">
            <label htmlFor="bind-scope">Scope</label>
            <select
              id="bind-scope"
              className="nx-select"
              value={form.scope}
              onChange={(event) => {
                setForm({ ...form, scope: event.target.value as BindingScope });
              }}
            >
              {SCOPES.filter((scope) => brain.binding_scopes.includes(scope)).map((scope) => (
                <option key={scope} value={scope}>
                  {scope.replace(/_/g, " ")}
                </option>
              ))}
            </select>
          </div>
          <div className="nx-field">
            <label htmlFor="bind-target">Target</label>
            <input
              id="bind-target"
              className="nx-input"
              required
              value={form.targetId}
              onChange={(event) => {
                setForm({ ...form, targetId: event.target.value });
              }}
            />
          </div>
          <div className="nx-field">
            <label htmlFor="bind-purpose">Purpose</label>
            <input
              id="bind-purpose"
              className="nx-input"
              required
              value={form.purpose}
              onChange={(event) => {
                setForm({ ...form, purpose: event.target.value });
              }}
            />
          </div>
          <div className="nx-field">
            <label htmlFor="bind-rationale">Rationale</label>
            <input
              id="bind-rationale"
              className="nx-input"
              required
              value={form.rationale}
              onChange={(event) => {
                setForm({ ...form, rationale: event.target.value });
              }}
            />
          </div>
          <button type="submit" className="nx-button nx-button--primary" disabled={busy}>
            Bind
          </button>
            </form>
      ) : (
        <div style={{ padding: "0 18px 18px" }}>
          {/*
            Binding only. The platform can close a binding
            (`POST /brains/bindings/withdraw`, guarded by the same
            `brain.binding.bind`), but this screen does not offer it, so it
            must not name it: a sentence promising an operation nobody can
            reach here is a false statement about the product.
          */}
          <RequiredAuthority capability="brain.binding.bind" what="Binding this capability" />
        </div>
      )}
    </Card>
  );
}

// ── definitions ───────────────────────────────────────────────────────────

function RegisterBrain({
  onRegistered,
  report,
}: {
  onRegistered: () => void;
  report: (outcome: Outcome) => void;
}) {
  const [form, setForm] = useState({
    key: "",
    displayName: "",
    purpose: "",
    family: "requirements" as CapabilityFamily,
    owner: "",
    risk: "routine" as RiskClass,
  });
  const [busy, setBusy] = useState(false);

  return (
    <form
      style={{ padding: "0 18px 18px" }}
      onSubmit={(event) => {
        event.preventDefault();
        setBusy(true);
        void change(
          (correlationId) =>
            api.post<BrainDefinition>(
              `${BRAIN}/definitions`,
              {
                key: form.key,
                display_name: form.displayName,
                purpose: form.purpose,
                capability_family: form.family,
                owner: form.owner,
                risk_class: form.risk,
                authority_ceiling: 1,
                classification_ceiling: "internal",
                binding_scopes: ["repository", "application"],
              },
              { correlationId },
            ),
          "Registering a capability",
          `${form.key} registered.`,
          refusalOnThisScreen,
        )
          .then((result) => {
            report(result);
            if (result.tone === "ok") {
              setForm({ ...form, key: "", displayName: "", purpose: "", owner: "" });
              onRegistered();
            }
          })
          .finally(() => {
            setBusy(false);
          });
      }}
    >
      <h3 style={{ fontSize: "var(--nx-text-base)", margin: "0 0 10px" }}>Register a capability</h3>
      <div className="nx-field">
        <label htmlFor="brain-key">Key</label>
        <input
          id="brain-key"
          className="nx-input"
          required
          value={form.key}
          onChange={(event) => {
            setForm({ ...form, key: event.target.value });
          }}
        />
      </div>
      <div className="nx-field">
        <label htmlFor="brain-name">Display name</label>
        <input
          id="brain-name"
          className="nx-input"
          required
          value={form.displayName}
          onChange={(event) => {
            setForm({ ...form, displayName: event.target.value });
          }}
        />
      </div>
      <div className="nx-field">
        <label htmlFor="brain-purpose">Purpose</label>
        <input
          id="brain-purpose"
          className="nx-input"
          required
          value={form.purpose}
          onChange={(event) => {
            setForm({ ...form, purpose: event.target.value });
          }}
        />
      </div>
      <div className="nx-field">
        <label htmlFor="brain-family">Capability family</label>
        <select
          id="brain-family"
          className="nx-select"
          value={form.family}
          onChange={(event) => {
            setForm({ ...form, family: event.target.value as CapabilityFamily });
          }}
        >
          {FAMILIES.map((family) => (
            <option key={family} value={family}>
              {family.replace(/_/g, " ")}
            </option>
          ))}
        </select>
      </div>
      <div className="nx-field">
        <label htmlFor="brain-owner">Owner</label>
        <input
          id="brain-owner"
          className="nx-input"
          required
          value={form.owner}
          onChange={(event) => {
            setForm({ ...form, owner: event.target.value });
          }}
        />
      </div>
      <div className="nx-field">
        <label htmlFor="brain-risk">Risk class</label>
        <select
          id="brain-risk"
          className="nx-select"
          value={form.risk}
          onChange={(event) => {
            setForm({ ...form, risk: event.target.value as RiskClass });
          }}
        >
          {RISKS.map((risk) => (
            <option key={risk} value={risk}>
              {risk}
            </option>
          ))}
        </select>
      </div>
      <button type="submit" className="nx-button nx-button--primary" disabled={busy}>
        Register
      </button>
    </form>
  );
}

export function Brains() {
  const session = useSession();
  const mayRead = session.may("brain.read");
  // A separate authority, and a separate question. The version list enforces
  // `brain.version.read`, so gating it on the definition key would offer a
  // workflow the server refuses (`NXP-AUTHZ-VOCAB-001`).
  const mayReadVersions = session.may("brain_version.read");
  const brains = useRegistryCollection<BrainDefinition>(`${BRAIN}/definitions`, mayRead);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);

  const brain = brains.items.find((item) => item.key === selectedKey) ?? null;
  const versions = useRegistryCollection<BrainVersion>(
    brain === null ? `${BRAIN}/definitions/none/versions` : `${BRAIN}/definitions/${brain.key}/versions`,
    mayReadVersions && brain !== null,
  );
  const version = versions.items.find((item) => item.version === selectedVersion) ?? null;

  if (!mayRead) {
    return (
      <Card title="Brains">
        <Forbidden what="the reasoning capabilities in this tenant" />
      </Card>
    );
  }

  return (
    <>
      <Banner title="A capability, its versions, and where it is selected.">
        Writing how a capability reasons and deciding it may take part in decisions are different
        jobs. The lifecycle below is shown to everyone; the actions on it are not.
      </Banner>
      <Result outcome={outcome} />
      <div style={{ display: "grid", gap: 18 }}>
        <Card
          title="Reasoning capabilities"
          actions={
            <button type="button" className="nx-button" onClick={brains.refresh}>
              Refresh
            </button>
          }
          bodyless
        >
          <Show
            resource={brains.state}
            what="the reasoning capabilities in this tenant"
            onRetry={brains.refresh}
            empty={() =>
              brains.items.length === 0 ? (
                <Empty
                  title="No capabilities registered."
                  hint="A definition says a capability exists and who is accountable for it."
                />
              ) : null
            }
          >
            {() => (
              <div className="nx-table-wrap">
                <table className="nx-table">
                  <caption className="nx-visually-hidden">Registered reasoning capabilities</caption>
                  <thead>
                    <tr>
                      <th scope="col">Capability</th>
                      <th scope="col">Family</th>
                      <th scope="col">Risk</th>
                      <th scope="col">Owner</th>
                      <th scope="col">State</th>
                      <th scope="col" />
                    </tr>
                  </thead>
                  <tbody>
                    {brains.items.map((item) => (
                      <tr key={item.brain_id}>
                        <td>
                          <span className="nx-table__primary">{item.display_name}</span>
                          <span className="nx-table__secondary" style={{ display: "block" }}>
                            <Mono>{item.key}</Mono>
                          </span>
                        </td>
                        <td>{item.capability_family.replace(/_/g, " ")}</td>
                        <td>
                          <Pill tone={item.risk_class === "critical" ? "bad" : item.risk_class === "elevated" ? "wait" : "quiet"}>
                            {item.risk_class}
                          </Pill>
                        </td>
                        <td>{item.owner}</td>
                        <td>
                          <Pill tone={item.lifecycle === "active" ? "ok" : "quiet"}>
                            {item.lifecycle}
                          </Pill>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="nx-button"
                            aria-pressed={selectedKey === item.key}
                            onClick={() => {
                              setSelectedKey(item.key);
                              setSelectedVersion(null);
                            }}
                          >
                            {selectedKey === item.key ? "Showing" : "Show"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Show>
          <More
            collection={brains}
            label="Show more capabilities"
            hint="This is one page of the registry, not all of it."
            placement="bodyless"
          />
          {session.may("brain.register") ? (
            <RegisterBrain onRegistered={brains.refresh} report={setOutcome} />
          ) : null}
        </Card>

        {brain === null ? (
          <Card title="A capability">
            <p className="nx-hint">Choose a capability to see how it reasons and where it is used.</p>
          </Card>
        ) : (
          <>
            <Card title={brain.display_name}>
              <Facts>
                <Fact label="Purpose">{brain.purpose}</Fact>
                <Fact label="Family">{brain.capability_family.replace(/_/g, " ")}</Fact>
                <Fact label="Owner">{brain.owner}</Fact>
                <Fact label="Risk class">{brain.risk_class}</Fact>
                <Fact label="Authority ceiling">
                  {AUTHORITY[brain.authority_ceiling] ?? String(brain.authority_ceiling)}
                </Fact>
                <Fact label="Classification ceiling">
                  <Classification value={brain.classification_ceiling} />
                </Fact>
                <Fact label="Bindable at">
                  {brain.binding_scopes.map((scope) => scope.replace(/_/g, " ")).join(" · ")}
                </Fact>
                <Fact label="Registered">
                  {when(brain.recorded_at)} by <Identifier value={brain.recorded_by} />
                </Fact>
              </Facts>
            </Card>

            <Versions
              brainKey={brain.key}
              selected={selectedVersion}
              onSelect={setSelectedVersion}
              collection={versions}
              report={setOutcome}
              mayRead={mayReadVersions}
            />

            {version === null ? null : (
              <VersionPanel
                brainKey={brain.key}
                version={version}
                onChanged={versions.refresh}
                report={setOutcome}
              />
            )}

            <Bindings brain={brain} report={setOutcome} />
          </>
        )}
      </div>
    </>
  );
}
